<template>
    <div class="rlms-flex rlms-flex-col rlms-gap-desk30-mob-20">
        <!-- Header -->
        <div class="rlms-flex rlms-items-center rlms-justify-between rlms-flex-wrap rlms-gap-10">
            <div class="rlms-fs-lg rlms-fw-600">Purchase History</div>
        </div>

        <!-- Loading Spinner -->
        <div v-if="loading" class="loading-spinner">
            <div class="spinner"></div>
            <p>Loading purchase history...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-container">
            <p class="error-message">{{ error }}</p>
            <button class="rlms-btn rlms-btn-primary" @click="fetchPurchaseHistory">Try Again</button>
        </div>

        <!-- Purchase History Table -->
        <div v-else class="rlms-table">
            <div class="rlms-table-inner">
                <table>
                    <thead>
                        <tr>
                            <th>Purchase Type</th>
                            <th>Amount</th>
                            <th>Date</th>
                            <th>Payment Type</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="4">
                                <SkeletonLoader />
                            </td>
                        </tr>
                        <template v-else>
                            <tr v-for="item in purchaseHistory" :key="item.id">
                                <td>{{ item?.product?.name ?? 'Unknown Product' }} x {{ item.learner_quantity }}</td>
                                <td>{{ formatCurrency(item.amount * item.learner_quantity) }}</td>
                                <td>{{ formatDate(item.created_at) }}</td>
                                <td>{{ item.mode === 'credit' ? 'Admin Credit' : item.mode === 'po' ? 'Purchase Order' : 'Stripe' }}</td>
                                <td>{{ item.status ? 'Paid' : item?.payment_status ?? 'Incomplete' }}</td>
                            </tr>
                            <tr v-if="purchaseHistory.length === 0">
                                <td colspan="4" class="text-center py-4">
                                    <div class="rlms-flex rlms-flex-col rlms-gap-30 rlms-text-center rlms-p-3">
                                        <img :src="noDataImg" class="rlms-mx-auto"
                                            style="width: clamp(150px, -1533rem + 3200vw, 150px);">
                                        <p class="rlms-text-center">No purchase history
                                            found.</p>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>
        <paginate v-model="page" :page-count="totalPages" :margin-pages="2" :prev-text="'<'" :next-text="'>'"
            :container-class="'pagination'" :page-class="'page-item'" :active-class="'active-page'"
            v-if="totalPages > 1" />
    </div>
</template>

<script setup>
import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue';
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { formatCurrency, formatDate } from '@/utils/format-helpers';
import { handleError } from '@/utils/handleError';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import noDataImg from '@/assets/images/no-data-img.png';
import paginate from 'vuejs-paginate-next';

const route = useRoute();
const organizationId = Number(route.params.organization ?? 0);
const purchaseHistory = ref([]);
const error = ref(null);

const page = ref(1);
const totalPages = ref(1);
const totalItems = ref(0);

const loader = useLoaderStore();

const loading = computed(() => {
    return loader.loading;
});

const fetchPurchaseHistory = async () => {
    error.value = null;
    loader.show();

    try {
        const { data } = await api.get(`/organizations/${organizationId}/purchase-history`, {
            params: {
                page: page.value,
                per_page: 10
            }
        });

        purchaseHistory.value = data?.data?.purchases.data ?? [];

        // Optional: store pagination meta if needed
        totalPages.value = data?.data?.purchases.last_page;
        totalItems.value = data?.data?.purchases.total;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

onMounted(fetchPurchaseHistory);

watch(page, async () => {
    await fetchPurchaseHistory();
});
</script>